import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import type { Security } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { SearchIcon } from 'components/Graph/GraphSidePanel/icons/SearchIcon';
import { useSecuritySchemaContextSelectors } from 'components/SecuritySchema/model/SecuritySchemaModel';
import { isListingScope } from 'components/SecuritySchema/model/SecuritySchemaScope';
import { openSecuritySchemaScope } from 'components/SecuritySchema/model/commands/openSecuritySchemaScope';
import {
  type SecurityEntityItem,
  selectVisibleRuleChainItems,
} from 'components/SecuritySchema/model/selectors/selectVisibleRuleChainItems';
import { TabView } from 'components/Tabs/TabView';
import { useLocale } from 'containers/App/LocaleContext';
import { useState } from 'react';
import type { FC } from 'react';
import type { Tab } from '../Tabs/useTabs';

interface SecurityEntityItemListProps {
  items: SecurityEntityItem[];
}

const SecurityEntityItemList: FC<SecurityEntityItemListProps> = ({ items }) => {
  const ctxProps = useSecuritySchemaContextSelectors({
    dispatch: prop('dispatch'),
  });
  const msgEntityList = useLocale().securitySchema.entityList;
  const [searchQuery, setSearchQuery] = useState('');
  const filteredItems = searchQuery
    ? items.filter((item) =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : items;

  return (
    <>
      <div className='input-group mb-5'>
        <SearchIcon
          title='Search'
          className='ipf-input-search-icon'
          width={16}
          height={16}
        />
        <input
          className='ipf-input ipf-input--search'
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          type='text'
          value={searchQuery}
        />
      </div>
      <ScrollView
        className='flex flex-1 flex-col'
        contentClassName='flex flex-col flex-1'
      >
        {filteredItems.length === 0 ? (
          <p>
            <em>
              {items.length > 0
                ? msgEntityList.emptyFiltered
                : msgEntityList.empty}
            </em>
          </p>
        ) : (
          filteredItems.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: no better index there
            <div key={index}>
              <ButtonAsA
                onClick={() => {
                  ctxProps.dispatch(openSecuritySchemaScope, {
                    scope: item.scope,
                  });
                }}
              >
                {item.name}
              </ButtonAsA>
            </div>
          ))
        )}
      </ScrollView>
    </>
  );
};

export const SecurityEntityList: FC = () => {
  const ctxProps = useSecuritySchemaContextSelectors({
    dispatch: prop('dispatch'),
    deviceSecurity: prop('deviceSecurity'),
    scope: prop('scope'),
    visibleRuleChainItems: selectVisibleRuleChainItems,
  });
  const msgEntityList = useLocale().securitySchema.entityList;

  const { scope } = ctxProps;

  if (!isListingScope(scope) || ctxProps.deviceSecurity === null) {
    stumble('Invalid state; cannot render entity list');
    return null;
  }

  const createEntityTab = (
    type: Security.SecurityType,
  ): Tab<Security.SecurityType> => {
    const items = ctxProps.visibleRuleChainItems[type];
    return {
      render: () => <SecurityEntityItemList items={items} />,
      disabled: items.length === 0,
      key: type,
      label: (
        <>
          {msgEntityList.title[type]}
          <div
            className={classNames(
              'badge badge-inline text-lgu-lg ml-2.5',
              ctxProps.visibleRuleChainItems[type].length === 0 &&
                'badge-light',
            )}
          >
            {ctxProps.visibleRuleChainItems[type].length}
          </div>
        </>
      ),
    };
  };

  // Cloud constructs additionally carry a cloud security machine — surface it as
  // an optional first tab so the user can browse the construct's cloud rule
  // chains alongside the (empty) on-prem machine tabs (NIM-24333).
  const hasCloudSecurity = ctxProps.deviceSecurity.machineCloud !== null;
  const tabs: Tab<Security.SecurityType>[] = [
    ...(hasCloudSecurity ? [createEntityTab('cloud')] : []),
    createEntityTab('acl'),
    createEntityTab('zoneFw'),
    createEntityTab('nat44'),
    createEntityTab('pbr'),
  ];

  const scopedActiveTab =
    scope.entityType !== undefined &&
    scope.entityType !== 'referencedTest' &&
    ctxProps.visibleRuleChainItems[scope.entityType].length > 0
      ? scope.entityType
      : null;
  // The on-prem tabs are empty for a cloud construct, so default to the cloud
  // tab when the scope hasn't selected a (non-empty) type.
  const activeTab =
    scopedActiveTab ??
    (hasCloudSecurity && ctxProps.visibleRuleChainItems.cloud.length > 0
      ? 'cloud'
      : null);

  return (
    <TabView
      activeTab={activeTab}
      contentClassName='px-lgu-6 py-lgu-10'
      onChangeTab={(tab) => {
        ctxProps.dispatch(openSecuritySchemaScope, {
          scope: {
            type: 'scopeList',
            entityType: tab,
          },
        });
      }}
      tabs={tabs}
      className='tabs-underline'
      variant='legacy'
    />
  );
};
